import { createSlice } from '@reduxjs/toolkit';
import { emit, emitJson, emitMsg } from '../websocket/websocketSlice'

const initialState = {
    numStudents: Number(localStorage.getItem('numStudents')) || 30,
    disorderLevel: Number(localStorage.getItem('disorderLevel')) || 3,
    startingSituation: JSON.parse(localStorage.getItem('startingSituation')) || 'seatwork',
    interventionControl: localStorage.getItem('interventionControl') || 'manually',
    noiseLevel: Number(localStorage.getItem('noiseLevel')) || 4,
    logFileName: localStorage.getItem('logFileName') || ' ',
    useEventLogging: JSON.parse(localStorage.getItem('useEventLogging')) || false,
    enableAllScripts: JSON.parse(localStorage.getItem('enableAllScripts')) || false,
    useSpeechTools: JSON.parse(localStorage.getItem('useSpeechTools')) || false,
}

/**
 * configurationSlice manages the global state for classroom session configuration using Redux Toolkit and local storage.
 * 
 * It handles the following configurations:
 * - Number of students.
 * - Level of disorder.
 * - Starting situation.
 * - Disruption intervention type.
 * - Noise level.
 * - Log file name.
 * - enable all possible scripts for logging
 * - use SpeechTools.
 * 
 * @property {number} numStudents - Number of students in the class.
 * @property {number} disorderLevel - The level of disorder in the classroom.
 * @property {boolean} startingSituation -  Seatwork vs. attention forward.
 * @property {string} interventionControl - The type of intervention (manually vs. hybrid) for disruptions.
 * @property {number} noiseLevel - The noise level in the classroom.
 * @property {string} logFileName - The name of the log file.
 * @property {boolean} autoselectAll - Whether all possible scripts should be enabledor not.
 * @property {boolean} useSpeechTools - Whether the sppech tools should be used as soon as possible.
 * 
 * @returns {object} - Actions to update each configuration, and selectors to access the current values.
 */
export const configurationSlice = createSlice({
    name: 'configuration',
    initialState,
    reducers: {
        setNumStudents: (state, action) => {
            state.numStudents = action.payload;
        },
        setDisorderLevel: (state, action) => {
            state.disorderLevel = action.payload;
        },
        setStartingSituation: (state, action) => {
            state.startingSituation = action.payload;
        },
        setInterventionControl: (state, action) => {
            state.interventionControl = action.payload;
        },
        setVolume: (state, action) => {
            const roundedValue = parseFloat(action.payload.toFixed(2));
            state.volume = roundedValue;
        },
        setNoiseLevel: (state, action) => {
            const roundedValue = parseFloat(action.payload.toFixed(2));
            state.noiseLevel = roundedValue;
        },
        setLogFileName: (state, action) => {
            state.logFileName = action.payload;
        },
        setEnableAllScripts: (state, action) => {
            state.enableAllScripts = action.payload;
        },
        setUseSpeechTools: (state, action) => {
            state.useSpeechTools = action.payload;
        },
        resetDefaults: (state) => {
            Object.keys(initialState).forEach((key) => {
                state[key] = initialState[key];
                localStorage.setItem(key, JSON.stringify(initialState[key]));
            });
        }
    },
});

export const getConfigurationPayload = (state) => {
    const numStudents = selectNumStudents(state);
    const disorderLevel = selectDisorderLevel(state);
    const startingSituation = selectStartingSituation(state);
    const interventionControl = selectInterventionControl(state);

    // Create JSON structure
    return {
        numStudents,
        disorderLevel,
        startingSituation,
        interventionControl
    };
}

export const getDataCollectionPayload = (state) => {
    const logfileName = selectLogFileName(state);
    const autoselectAll = selectEnableAllScripts(state);

    return {
        logfileName,
        autoselectAll
    };
}

export const getSpeechtoolPayload = (state) => {
    const enableSpeechtools = selectUseSpeechTools(state);

    return { enableSpeechtools };
}

/**
 * This function is currently handled in CoachRTC. As MenuDataHolder and 
 * associated scripts no longer work, they currently have no effect.
 * @returns string (json) : {"numStudents":int,"disorderLevel":int,"startingSituation":"seatwork" vs "attentionForward","interventionControl":"manually" vs "hybrid","noiseLevel":int}
 */
export const sendConfigurationParameters = () => (dispatch, getState) => {
    const state = getState();
    const parameters = getConfigurationPayload(state);
    const noiseLevel = selectNoiseLevel(state);
    emit({ type: "configurationParameters", ...parameters });
    emit({ type: "changeNoiseLevel", noiseLevel });
};

// @returns string (json) : {"logfileName":string,"autoselectAll":bool}
export const sendDataCollectionParameters = () => (dispatch, getState) => {
    const parameters = getDataCollectionPayload(getState());
    emit({ type: "dataCollectionParameters", ...parameters });
};

export const sendSpeechtoolsParameters = () => (dispatch, getState) => {
    const parameters = getSpeechtoolPayload(getState());
    emit({ type: "speechtoolParameters", ...parameters });
};

export const {
    setNumStudents,
    setDisorderLevel,
    setStartingSituation,
    setInterventionControl,
    setNoiseLevel,
    setLogFileName,
    setEnableAllScripts,
    setUseSpeechTools,
    resetDefaults
} = configurationSlice.actions;

// Selectors to get the states from the Redux Store
export const selectNumStudents = (state) => state.configuration.numStudents;
export const selectDisorderLevel = (state) => state.configuration.disorderLevel;
export const selectStartingSituation = (state) => state.configuration.startingSituation;
export const selectInterventionControl = (state) => state.configuration.interventionControl;
export const selectNoiseLevel = (state) => state.configuration.noiseLevel;
export const selectLogFileName = (state) => state.configuration.logFileName;
export const selectEnableAllScripts = (state) => state.configuration.enableAllScripts;
export const selectUseSpeechTools = (state) => state.configuration.useSpeechTools;

export default configurationSlice.reducer;